import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  VStack,
  Link,
  Box,
  useDisclosure,
  useTheme,
  Flex,
  CloseButton,
  Center,
  Image
} from '@chakra-ui/react'
import { NEW_PLATFORM_URL } from '../../../../utils/constants'
import { faArrowUp, faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { Button } from '../../../../components/Button'

import { useAuth } from '../../../../context/auth-context'
import { useFeatureFlags } from '../../../../context/feature-flag-context'
import { useRole } from '../../../../utils/rbac'

const { REACT_APP_SHUTDOWN_METHOD, REACT_APP_SHUTDOWN_LOCATIONS } = process.env
const MIGRATED_LOCATIONS =
  REACT_APP_SHUTDOWN_LOCATIONS !== undefined
    ? REACT_APP_SHUTDOWN_LOCATIONS.split(',').map((id) => parseInt(id))
    : []

const PAGE_BLOCK_METHOD: 'modal' | 'page_block' =
  REACT_APP_SHUTDOWN_METHOD === 'page_block' ? 'page_block' : 'modal'

export const NewPlatformModal = ({ isOpen, onClose }) => {
  const handleGoToPlatform = () => {
    window.open(NEW_PLATFORM_URL, '_blank', 'noopener,noreferrer')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.600" />

      <ModalContent borderRadius="xl" mx={4}>
        <ModalCloseButton />

        <ModalHeader pt={8} px={8} pb={2}>
          We've moved to a new platform
        </ModalHeader>

        <ModalBody px={8} py={6}>
          <VStack align="stretch" spacing={5}>
            <Text fontSize="md" color="gray.600" lineHeight="1.7">
              We’re excited to let you know that we’ve moved to a brand new
              platform. You can now manage all aspects of your membership, book
              rooms, day passes, and stay updated with the latest news from the
              community.
            </Text>

            <Box
              bg="gray.50"
              borderRadius="lg"
              p={5}
              borderWidth="1px"
              borderColor="gray.100"
            >
              <Text fontWeight="600" mb={2}>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  style={{
                    marginRight: '4px'
                  }}
                />
                Look for an invitation in your email
              </Text>

              <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                You should have received an invitation from the team with
                instructions to sign in and access your account.
              </Text>
            </Box>

            <Text fontSize="sm" color="gray.500">
              If you can't find the invitation, please check your spam or junk
              folder.
            </Text>

            <Text fontSize="sm" color="gray.500">
              You can access the new platform directly here:{' '}
              <Link
                href={NEW_PLATFORM_URL}
                isExternal
                color="blue.500"
                fontWeight="600"
              >
                os.spacecubed.com
              </Link>
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter px={8} pb={8}>
          <Button
            width="100%"
            size="lg"
            rightIcon={<FontAwesomeIcon icon={faArrowUp} />}
            onClick={handleGoToPlatform}
          >
            Go to the new platform
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export const NewPlatformWrapper = ({
  children
}: {
  children: React.ReactNode
}) => {
  const { currentOrganization, me } = useAuth()
  const { displayMigrationModal } = useFeatureFlags()
  const theme: any = useTheme()
  const [displayBanner, setDisplayBanner] = useState(false)
  const { isInAdminRole } = useRole()

  const {
    isOpen: isNewPlatformModalOpen,
    onOpen: onOpenNewPlatformModal,
    onClose: onCloseNewPlatformModal
  } = useDisclosure()

  useEffect(() => {
    if (currentOrganization && displayMigrationModal) {
      if (MIGRATED_LOCATIONS.includes(currentOrganization.home_location?.id)) {
        setDisplayBanner(true)
        if (PAGE_BLOCK_METHOD === 'modal') {
          onOpenNewPlatformModal()
        }
      }
    }
  }, [currentOrganization, displayMigrationModal])

  if (displayBanner && PAGE_BLOCK_METHOD === 'page_block') {
    return (
      <Center h="100vh" w="100vw" bg={'white'}>
        <VStack spacing={4} maxWidth="600px" px={4} textAlign="center">
          <Flex justifyContent="space-between" w="100%">
            <Image
              src={theme.logos.logo_long}
              width="100%"
              alignSelf="center"
              maxWidth="170px"
            />
            {isInAdminRole && (
              <CloseButton onClick={() => setDisplayBanner(false)} />
            )}
          </Flex>
          <Flex justifyContent="center" w="100%"></Flex>

          <Text fontSize="xl" fontWeight="bold">
            Hello, {me?.user?.name}!
          </Text>
          <Text fontSize="md" color="gray.600" lineHeight="1.7">
            We’re excited to let you know that we’ve moved to a brand new
            platform. You can now manage all aspects of your membership, book
            rooms, day passes, and stay updated with the latest news from the
            community.
          </Text>
          <Box
            bg="gray.50"
            borderRadius="lg"
            p={5}
            borderWidth="1px"
            borderColor="gray.100"
          >
            <Text fontWeight="600" mb={2}>
              <FontAwesomeIcon
                icon={faEnvelope}
                style={{
                  marginRight: '4px'
                }}
              />
              Look for an invitation in your email
            </Text>

            <Text fontSize="sm" color="gray.600" lineHeight="1.6">
              You should have received an invitation from the team with
              instructions to sign in and access your account.
            </Text>
          </Box>
          <Text color="gray.600" fontSize="2xl" fontWeight="bold">
            You can access the new platform directly here{' '}
            <Link
              href={NEW_PLATFORM_URL}
              isExternal
              color={theme.colors.brandPrimary}
              fontWeight="bold"
              textDecoration={'underline'}
            >
              os.spacecubed.com
            </Link>
          </Text>
        </VStack>
      </Center>
    )
  }

  return (
    <>
      {displayBanner && (
        <Flex
          align="center"
          w="100%"
          p={5}
          bg={theme.colors.brandPrimary}
          flexWrap="wrap"
          gap={2}
        >
          {isInAdminRole && (
            <CloseButton
              color="white"
              onClick={() => setDisplayBanner(false)}
            />
          )}
          <Text color="white">
            New platform available at{' '}
            <Link
              href={NEW_PLATFORM_URL}
              isExternal
              color="white"
              fontWeight="bold"
              textDecoration={'underline'}
            >
              os.spacecubed.com
            </Link>
          </Text>
          <Text color="white">
            <FontAwesomeIcon
              icon={faEnvelope}
              style={{
                marginRight: '4px'
              }}
            />{' '}
            Check your email for an invitation to access your account on the new
            platform.
          </Text>
        </Flex>
      )}
      {children}
      <NewPlatformModal
        isOpen={isNewPlatformModalOpen}
        onClose={onCloseNewPlatformModal}
      />
    </>
  )
}
